import type { ComponentType, ReactNode } from 'react'

/**
 * The page-header art set — a SECOND icon system, deliberately unlike the rail's.
 *
 * ── Why two systems and not one ──
 * The rail and the page header were drawing the same glyph, on the argument that the tile you
 * clicked and the tile at the top of the page should be the same object. That argument is
 * about CONTINUITY, and it is satisfied by the two icons sharing a subject — they do not have
 * to share a rendering.
 *
 * The reference (defi.pots.money) runs exactly this split, and it is worth stating precisely
 * because it is the whole basis of this file:
 *
 *   rail    inline SVG, 24x24, stroke-only line art at stroke-width 2, tinted when active
 *   header  a filled two-tone illustration at 40x40, no container, sitting on the page ground
 *
 * The reason it works is that the two are answering different questions. A rail icon is one
 * of twelve, repeated on every route, read at 18px in peripheral vision — it has to be light,
 * uniform and instantly scannable, which is what line art is for. A header icon appears ONCE
 * per page at 40px and is the first thing on the screen; it can afford mass and depth, and a
 * hairline outline there just looks like a rail icon that got enlarged.
 *
 * ── What is copied and what is not ──
 * The CONSTRUCTION is copied: solid fills, no strokes, a saturated base plus a pale highlight,
 * a vertical gradient for dimension. The palette is not — theirs is green on green, ours is
 * the accent blue against a near-white highlight, which is the same relationship in our own
 * colours.
 *
 * Their art also ships as raster PNGs at 112px, rendered at 40. These are inline SVG instead:
 * they scale to any density, cost no extra request, and follow the theme, none of which a
 * flattened PNG can do.
 *
 * ── Conventions, so a fourteenth matches the thirteen ──
 *   · 24x24 viewBox, matching `icons.tsx`, so the two sets are interchangeable in a pinch and
 *     the fallback at the bottom of this file cannot land on a mismatched box.
 *   · Fills only. No stroke anywhere — a stroke is the rail's language and mixing them is what
 *     makes an icon set look assembled rather than drawn.
 *   · Exactly two fills: `url(#<id>-b)` for the body, `url(#<id>-h)` for the highlight. More
 *     than two and the form stops reading at 40px.
 *   · Every gradient id is prefixed with the icon's own name. Two of these can appear on one
 *     screen (a page header and a card), and duplicate SVG gradient ids in a document silently
 *     resolve to whichever came first — the second icon then renders in the first one's
 *     colours, which is invisible in review and obvious in production.
 */

/** The blue body ramp and the pale highlight, defined once per icon under a unique id. */
function Defs({ id }: { id: string }) {
  return (
    <defs>
      {/*
       * Light at the top, deep at the bottom. The stops are the accent and the brand blue, so
       * the art is built from the same two blues as the buttons rather than from a gradient
       * invented for it.
       */}
      <linearGradient id={`${id}-b`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="var(--color-accent)" />
        <stop offset="1" stopColor="var(--color-accent-deep, var(--color-accent))" />
      </linearGradient>
      {/*
       * The highlight is near-white rather than a lighter blue. A lighter blue reads as the
       * same material catching light; a pale, slightly cool white reads as a DIFFERENT
       * material — glass against enamel — which is what gives these their depth at 40px.
       */}
      <linearGradient id={`${id}-h`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="1" stopColor="#cfe4f7" />
      </linearGradient>
    </defs>
  )
}

function Art({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <Defs id={id} />
      {children}
    </svg>
  )
}

/** Home — the manifold: three legs arriving at one hub. Matches the rail's subject. */
export const ArtHome = ({ className }: { className?: string }) => (
  <Art id="a-home" className={className}>
    <path
      fill="url(#a-home-b)"
      d="M11.1 2.6h1.8v7.2h-1.8zM4.3 18.1l6.2-3.6.9 1.6-6.2 3.6zM19.7 18.1l-6.2-3.6-.9 1.6 6.2 3.6z"
    />
    <circle cx="12" cy="12" r="4.1" fill="url(#a-home-h)" />
  </Art>
)

/** Swap — two streams crossing. */
export const ArtSwap = ({ className }: { className?: string }) => (
  <Art id="a-swap" className={className}>
    <path fill="url(#a-swap-b)" d="M3 7.4h13.2v2.6H3zM16.2 5l4.4 3.7-4.4 3.7z" />
    <path fill="url(#a-swap-h)" d="M21 14h-13.2v2.6H21zM7.8 11.6 3.4 15.3l4.4 3.7z" />
  </Art>
)

/** Deposit — an inlet valve: flow arriving at a seat. */
export const ArtDeposit = ({ className }: { className?: string }) => (
  <Art id="a-deposit" className={className}>
    <path fill="url(#a-deposit-b)" d="M2.6 10.7h18.8v2.6H2.6zM10.7 2.6h2.6v6.9h-2.6z" />
    <circle cx="12" cy="17" r="4.4" fill="url(#a-deposit-h)" />
  </Art>
)

/** Bond — a sealed certificate. */
export const ArtBond = ({ className }: { className?: string }) => (
  <Art id="a-bond" className={className}>
    <rect x="3.2" y="4.4" width="17.6" height="13.2" rx="2.4" fill="url(#a-bond-b)" />
    <path fill="url(#a-bond-h)" d="M6.4 8.2h8v1.9h-8zM6.4 12h5.4v1.9H6.4z" />
  </Art>
)

/** Positions — stacked holdings. */
export const ArtPositions = ({ className }: { className?: string }) => (
  <Art id="a-positions" className={className}>
    <rect x="3" y="7.6" width="14" height="13.4" rx="2.3" fill="url(#a-positions-b)" />
    <rect x="7" y="3" width="14" height="13.4" rx="2.3" fill="url(#a-positions-h)" />
  </Art>
)

/** Lite — one lever, one setting. */
export const ArtLite = ({ className }: { className?: string }) => (
  <Art id="a-lite" className={className}>
    <path fill="url(#a-lite-b)" d="M2.6 10.7h18.8v2.6H2.6z" />
    <circle cx="15.4" cy="12" r="4.3" fill="url(#a-lite-h)" />
  </Art>
)

/** Lend — a reservoir at a level. */
export const ArtLend = ({ className }: { className?: string }) => (
  <Art id="a-lend" className={className}>
    <path fill="url(#a-lend-b)" d="M3.4 4.2h17.2v15.6a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2z" />
    <path fill="url(#a-lend-h)" d="M3.4 11.4h17.2v3.1H3.4z" />
  </Art>
)

/** Borrow — capital drawn down and out. */
export const ArtBorrow = ({ className }: { className?: string }) => (
  <Art id="a-borrow" className={className}>
    <path fill="url(#a-borrow-b)" d="M10.6 2.6h2.8v10.2h-2.8z" />
    <path fill="url(#a-borrow-h)" d="M12 20.4 5.6 12.6h12.8z" />
  </Art>
)

/**
 * Multiply — a loop around a core.
 *
 * ── Why the head is in the same path as the arc ──
 * Two attempts drew the arrowhead as a separate element and both rendered DETACHED, floating
 * clear of the arc's mouth. Chasing that by nudging coordinates is guesswork: the mouth of an
 * annulus is where two arcs of different radii terminate, so its exact position depends on the
 * sweep flags as much as on the numbers, and a value that looks right in the file can still
 * land in space.
 *
 * Putting the head in the SAME path as the body removes the failure mode instead of tuning it.
 * The subpaths overlap around the mouth and fill as one shape, so the head cannot separate no
 * matter what the arc geometry does. The highlight then moves to the core, which is a better
 * reading anyway: a loop running around something, rather than a loop with a light patch on it.
 */
export const ArtMultiply = ({ className }: { className?: string }) => (
  <Art id="a-multiply" className={className}>
    <path
      fill="url(#a-multiply-b)"
      d="M12 3.1a8.9 8.9 0 1 1-8.9 8.9h3.3A5.6 5.6 0 1 0 12 6.4zM11 1.1 17.6 4.9 11 8.7z"
    />
    <circle cx="12" cy="12" r="2.9" fill="url(#a-multiply-h)" />
  </Art>
)

/** Markets — a bank of levels. */
export const ArtMarkets = ({ className }: { className?: string }) => (
  <Art id="a-markets" className={className}>
    <path fill="url(#a-markets-b)" d="M3 13.6h4.2v7.4H3zM9.9 8.6h4.2v12.4H9.9z" />
    <path fill="url(#a-markets-h)" d="M16.8 3.6H21V21h-4.2z" />
  </Art>
)

/**
 * bNDX — a minted token: a struck rim around a pale core.
 *
 * The first attempt was a disc with two vertical bars on it, and at 40px it read as a media
 * PAUSE BUTTON. Worth recording, because it is the failure mode this whole set is exposed to:
 * two plain fills inside a circle is also the construction of half the transport controls
 * ever drawn, and the resemblance is invisible while you are thinking about the metaphor and
 * obvious the moment you see it rendered.
 *
 * The rim is an even-odd path rather than two stacked circles, so the core is genuinely a
 * separate shape and the ring keeps its weight when the highlight ramp changes.
 */
export const ArtBndx = ({ className }: { className?: string }) => (
  <Art id="a-bndx" className={className}>
    <path
      fill="url(#a-bndx-b)"
      fillRule="evenodd"
      d="M12 2.9a9.1 9.1 0 1 1 0 18.2 9.1 9.1 0 0 1 0-18.2m0 3.4a5.7 5.7 0 1 0 0 11.4 5.7 5.7 0 0 0 0-11.4"
    />
    <circle cx="12" cy="12" r="5.7" fill="url(#a-bndx-h)" />
  </Art>
)

/** Proof — a stamped seal. */
export const ArtProof = ({ className }: { className?: string }) => (
  <Art id="a-proof" className={className}>
    <circle cx="12" cy="12" r="9.1" fill="url(#a-proof-b)" />
    <path fill="url(#a-proof-h)" d="m10.9 15.4-3.3-3.3 1.7-1.7 1.6 1.6 4.1-4.1 1.7 1.7z" />
  </Art>
)

/** Portfolio — a holder's own book. */
export const ArtPortfolio = ({ className }: { className?: string }) => (
  <Art id="a-portfolio" className={className}>
    <rect x="2.8" y="6.6" width="18.4" height="14.6" rx="2.4" fill="url(#a-portfolio-b)" />
    <path fill="url(#a-portfolio-h)" d="M8.6 2.8h6.8a2 2 0 0 1 2 2v1.8H6.6V4.8a2 2 0 0 1 2-2z" />
  </Art>
)

/**
 * Icon id -> header art.
 *
 * Keyed by the stable string each icon carries (see `NavIcon` in ui/icons.tsx), NOT by the
 * component reference. An identity-keyed map was tried first and failed silently twice over:
 * hot reload swaps the module so the keys stop matching, and code-splitting can produce two
 * copies of the same component. Both render the line icon in the header and look deliberate.
 *
 * Pages never write these ids — they pass the icon and the id rides along — so there is still
 * nothing to misspell at a call site. `PageHeader` falls back to the passed icon when a
 * destination has no art, so a new route never renders an empty header.
 */
export const PAGE_ART: Record<string, ComponentType<{ className?: string }>> = {
  home: ArtHome,
  swap: ArtSwap,
  deposit: ArtDeposit,
  bond: ArtBond,
  positions: ArtPositions,
  lite: ArtLite,
  lend: ArtLend,
  borrow: ArtBorrow,
  multiply: ArtMultiply,
  markets: ArtMarkets,
  bndx: ArtBndx,
  proof: ArtProof,
  portfolio: ArtPortfolio,
}
